import React from 'react'
import { Spinner } from 'react-bootstrap'
import { SearchType } from './hooks/useMobileSearchAPI'
import { MobileSearchResultCard } from './MobileSearchResultCard'
import { MobileSearchEmptyState } from './MobileSearchEmptyState'
import { formatInteger } from '../../utils/formatting'
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll'
import { ResultsContainer, LoadingContainer } from './MobileSearch.styles'
import {
  ContactCompanySearchResult,
  DoorSearchResult,
} from '../../models/companies'

interface MobileSearchResultsProps {
  results: any[]
  total: number
  query: string
  searchType: SearchType
  isLoading: boolean
  isLoadingMore: boolean
  hasMore: boolean
  error: string | null
  onLoadMore: () => void
  isDebouncing: boolean
  onClick?: (company: ContactCompanySearchResult | DoorSearchResult) => void
}

export const MobileSearchResults: React.FC<MobileSearchResultsProps> = ({
  results,
  total,
  query,
  searchType,
  isLoading,
  isLoadingMore,
  hasMore,
  error,
  onLoadMore,
  isDebouncing,
  onClick,
}) => {
  // Use containerRef to observe scroll within ResultsContainer
  const { containerRef, sentinelRef } = useInfiniteScroll({
    onLoadMore,
    isLoading: isLoadingMore,
    hasMore,
    threshold: 100, // Load when 100px from bottom
  })

  // Show empty state if no query
  if (!query || query.length < 2) {
    return (
      <ResultsContainer>
        <MobileSearchEmptyState type="no-query" />
      </ResultsContainer>
    )
  }

  // Show loading state (check BEFORE error to prevent showing stale errors during new search)
  if (isLoading || isDebouncing) {
    return (
      <ResultsContainer>
        <LoadingContainer>
          <Spinner animation="border" />
        </LoadingContainer>
      </ResultsContainer>
    )
  }

  // Show error state
  if (error) {
    return (
      <ResultsContainer>
        <MobileSearchEmptyState type="error" errorMessage={error} />
      </ResultsContainer>
    )
  }

  // Show no results state
  if (results.length === 0) {
    return (
      <ResultsContainer>
        <MobileSearchEmptyState type="no-results" query={query} />
      </ResultsContainer>
    )
  }

  // Show results
  return (
    <ResultsContainer ref={containerRef}>
      <div
        className="px-2 py-1 text-xs text-fg-secondary bg-white"
        aria-live="polite"
        aria-atomic="true"
      >
        Showing {formatInteger(results.length)} of {formatInteger(total)} result
        {total === 1 ? '' : 's'}
      </div>
      <div className="px-2 py-2 pb-20" role="list" aria-label="Search results">
        {results.map((result, index) => {
          // Create a unique key that combines ID and index to handle potential duplicates
          const uniqueKey = `${result.id || result.item_id || 'result'}-${index}`
          return (
            <MobileSearchResultCard
              key={uniqueKey}
              result={result}
              searchType={searchType}
              onClick={onClick}
            />
          )
        })}
        {/* Sentinel element for IntersectionObserver - triggers load when visible */}
        <div ref={sentinelRef} style={{ height: '1px' }} />
      </div>
      {isLoadingMore && (
        <LoadingContainer>
          <Spinner animation="border" size="sm" />
        </LoadingContainer>
      )}
    </ResultsContainer>
  )
}
